import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { noop } from 'lodash-es';
import type {
  BottomProps,
  DisplayProps,
  LeftProps,
  MaxWidthProps,
  PositionProps,
  RightProps,
  TopProps,
  WidthProps,
} from 'styled-system';
import { bottom, display, left, maxWidth, position, right, top, width } from 'styled-system';

import type { CarouselItemProps } from '../../../../types/carousel-item';
import type { CarouselControlsProps } from '../../../Carousel';
import Carousel from '../../../Carousel';
import { Flex } from '../../../Flex';
import { NextCarouselButton, PrevCarouselButton } from '../BigImageHorizontalCarousel/Controls';
import { SLIDE_CELL_SPACING } from './constants';
import ThumbnailCarouselSlide from './ThumbnailCarouselSlide';

interface ThumbnailCarouselProps {
  activeItem?: number;
  items?: CarouselItemProps[];
  withFixedSize?: boolean;
  ariaLabel?: string;
  updateActiveItem?: (index: number) => void;
  baseId: string;
  onAfterSlide?: () => void;
}

const ThumbnailCarouselContainer = styled(Flex)<MaxWidthProps>`
  ${maxWidth};
`;

const thumbnailCarouselContainerStyles: ComponentProps<typeof ThumbnailCarouselContainer> = {
  maxWidth: [null, 506, 802, 1104],
};

const Container = styled.div<WidthProps & PositionProps>`
  ${width};
  ${position};
`;

const containerStyles: ComponentProps<typeof Container> = {
  width: ['100%', 'initial'],
  position: 'relative',
};

const ControlsContainer = styled.div<
  PositionProps & DisplayProps & TopProps & LeftProps & RightProps & BottomProps
>`
  ${position};
  ${display};
  ${top};
  ${left};
  ${right};
  ${bottom};
  ${display};
`;

const controlsContainerStyles: ComponentProps<typeof ControlsContainer> = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: ['none', 'block'],
};

const ThumbnailControls = ({
  hasPrev,
  hasNext,
  activeItem,
  updateActiveItem,
  getPrevIndex,
  getNextIndex,
}: CarouselControlsProps & Pick<ThumbnailCarouselProps, 'activeItem' | 'updateActiveItem'>) => (
  <ControlsContainer {...controlsContainerStyles}>
    {hasPrev && (
      <PrevCarouselButton
        onClick={() => updateActiveItem(getPrevIndex(activeItem))}
        disabled={!hasPrev}
      />
    )}
    {hasNext && (
      <NextCarouselButton
        onClick={() => updateActiveItem(getNextIndex(activeItem))}
        disabled={!hasNext}
      />
    )}
  </ControlsContainer>
);

const ThumbnailCarousel = ({
  items,
  updateActiveItem,
  activeItem,
  withFixedSize,
  ariaLabel,
  baseId,
  onAfterSlide = noop,
}: ThumbnailCarouselProps) => {
  return (
    <Container {...containerStyles}>
      <ThumbnailCarouselContainer
        {...thumbnailCarouselContainerStyles}
        data-testid="thumbnail-carousel-container"
      >
        <Carousel
          slideGap={SLIDE_CELL_SPACING}
          renderControls={props => (
            <ThumbnailControls
              activeItem={activeItem}
              updateActiveItem={updateActiveItem}
              {...props}
            />
          )}
          ariaProps={{ role: 'tablist' }}
          activeIndex={activeItem}
          afterSlide={onAfterSlide}
        >
          {items.map((data, index) => (
            <ThumbnailCarouselSlide
              key={index}
              data={data}
              index={index}
              onClick={updateActiveItem}
              withFixedSize={withFixedSize}
              activeItem={activeItem}
              baseId={baseId}
              count={items.length}
              {...(ariaLabel && { ariaLabel })}
            />
          ))}
        </Carousel>
      </ThumbnailCarouselContainer>
    </Container>
  );
};

export default ThumbnailCarousel;
